<template>
  <template v-if="isVisible">
    <Modal
      :show="true"
      :title="modalTitle"
      size="small"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="isSaveDisabled"
      :save-fn="handleSubmit"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <despesas-status-form
        ref="formRef"
        :is-edit-mode="isEditMode"
        :status="status"
        @save-success="handleSaveSuccess">
      </despesas-status-form>
    </Modal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/ui/Modal.vue';
import DespesasStatusForm from './DespesasStatusForm.vue';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface DespesaStatus {
  despesa_status_id: Id;
  despesa_status_titulo?: string;
  [key: string]: unknown;
}

type ModalMode = 'create' | 'edit';

interface ModalOpenPayload {
  mode?: ModalMode;
  status?: DespesaStatus | null;
}

interface DespesasStatusFormInstance {
  canSave: boolean;
  saveStatus: () => Promise<void>;
}

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const mode = ref<ModalMode>('create');
const status = ref<DespesaStatus | null>(null);
const formRef = ref<DespesasStatusFormInstance | null>(null);

const modalTitle = computed<string>(() => {
  return mode.value === 'create' ? t('Novo Status') : t('Editar Status');
});

const isEditMode = computed<boolean>(() => mode.value === 'edit');

const canSave = computed<boolean>(() => {
  return formRef.value?.canSave || false;
});

const isSaveDisabled = (): boolean => !canSave.value;

const handleClose = (): void => {
  log('DespesasStatusModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  mode.value = 'create';
  status.value = null;
};

const handleSubmit = async (): Promise<void> => {
  if (!formRef.value) {
    log('DespesasStatusModal', 'handleSubmit', 'Form ref not available');
    return;
  }

  log('DespesasStatusModal', 'handleSubmit', 'Calling form saveStatus');

  try {
    await formRef.value.saveStatus();
    log('DespesasStatusModal', 'handleSubmit', 'Form submitted successfully');
    eventBus.emit('despesas-status.saved');
    handleClose();
  } catch (error) {
    log('DespesasStatusModal', 'handleSubmit', 'Error submitting form', error);
    const errorMessage = mode.value === 'create'
      ? t('Erro ao criar status')
      : t('Erro ao atualizar status');
    const detail = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', errorMessage + ': ' + detail);
  }
};

const handleSaveSuccess = (): void => {
  log('DespesasStatusModal', 'handleSaveSuccess', 'Save success event received');
  eventBus.emit('despesas-status.saved');
  handleClose();
};

const handleOpen = (data?: ModalOpenPayload): void => {
  log('DespesasStatusModal', 'handleOpen', 'Opening modal via eventBus', data);
  mode.value = data?.mode || 'create';
  status.value = data?.status || null;
  isVisible.value = true;
};

onMounted(() => {
  log('DespesasStatusModal', 'onMounted', 'Modal mounted, listening for events');
  eventBus.on('modal.despesas-status.open', handleOpen);
});

onBeforeUnmount(() => {
  log('DespesasStatusModal', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('modal.despesas-status.open', handleOpen);
});
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

.text-danger {
  color: #dd4b39;
}

.input-group-addon {
  background-color: #f4f4f4;
  border: 1px solid #d2d6de;
  border-left: 0;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: #555;
  text-align: center;
}
</style>
